import i18n from "i18next";
import { getEnvironment } from "@packages/utils";
import axios, { AxiosRequestConfig, AxiosError } from "axios";
import { getAuth } from "firebase/auth";

export const getApiUrl = () => {
  //return "http://localhost:8000";
  return getEnvironment() === "DEV"
    ? "https://api-dev.madlen.io/api"
    : "https://api.madlen.io/api";
};

// Create a single axios instance
export const AXIOS_INSTANCE = axios.create({
  baseURL: getApiUrl(),
  headers: {
    "Content-Type": "application/json",
    UserRole: "TEACHER",
    MadlenAppName: "LTA",
  },
  paramsSerializer: {
    indexes: null, // no brackets at all
  },
});

let globalToken: string | null = null;
let globalOrganizationId: number | null = 1; // organization id default 1
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

export const setGlobalTokenAndOrganizationId = (
  token: string,
  organizationId: number,
) => {
  globalToken = token;
  globalOrganizationId = organizationId;
};

const refreshToken = async (): Promise<string> => {
  const auth = getAuth();

  // Wait for auth to initialize and get current user
  await new Promise((resolve) => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      unsubscribe();
      resolve(user);
    });
  });

  const user = auth.currentUser;
  if (!user) {
    throw new Error("No user found");
  }
  const newToken = await user.getIdToken(true); // Force refresh
  return newToken;
};

const subscribeTokenRefresh = (cb: (token: string) => void) => {
  refreshSubscribers.push(cb);
};

const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
};

// Custom instance function with cancellation support
export const configureAxios = <T>(config: AxiosRequestConfig): Promise<T> => {
  const source = axios.CancelToken.source();

  const customHeaders = {
    ...(!config.headers?.["Content-Type"]?.includes("multipart/form-data") && {
      "Content-Type": "application/json",
    }),
    UserRole: "TEACHER",
    MadlenAppName: "LTA",
    ...(globalToken ? { Authorization: `Bearer ${globalToken}` } : {}),
    ...(globalOrganizationId ? { OrganizationId: globalOrganizationId } : {}),
  };

  // Create the promise chain
  const promise = new Promise<T>((resolve, reject) => {
    const makeRequest = (token?: string) => {
      if (token) {
        customHeaders["Authorization"] = `Bearer ${token}`;
      }

      AXIOS_INSTANCE({
        ...config,
        headers: { ...config.headers, ...customHeaders },
        cancelToken: source.token,
      })
        .then(({ data }) => resolve(data))
        .catch(async (error: AxiosError) => {
          if (error.response?.status === 502) {
            window.location.href = "/maintenance";
            return;
          }
          // Handle 401 error
          if (
            error.response?.status === 401 ||
            error.response?.status === 403
          ) {
            // if error comes from google drive api we need to ignore it
            const isGoogleDriveEndpoint =
              error.response?.config?.url?.includes("drive-files-metadata") ||
              error.response?.config?.url?.includes(
                "drive-pdf-videos-metadata",
              ) ||
              error.response?.config?.url?.includes("drive-videos-metadata");
            if (isGoogleDriveEndpoint) {
              reject(error);
              return;
            }
            if (!isRefreshing) {
              isRefreshing = true;

              try {
                const newToken = await refreshToken();
                isRefreshing = false;
                globalToken = newToken;
                setGlobalTokenAndOrganizationId(
                  newToken,
                  globalOrganizationId!,
                );
                onTokenRefreshed(newToken);

                // Retry the original request with new token
                makeRequest(newToken);
                const teacher = await configureAxios({
                  url: `/teachers/me`,
                  method: "GET",
                });
                const state = {
                  loading: false,
                  token: newToken,
                  teacher: teacher,
                  organizationId: globalOrganizationId!,
                  isAuthenticated: true,
                };
                // Save to localStorage
                localStorage.setItem("userState", JSON.stringify(state));
              } catch (refreshError) {
                isRefreshing = false;
                localStorage.removeItem("userState");
                // Handle refresh token failure (e.g., redirect to login)
                window.location.href = "/login";
                reject(refreshError);
              }
            } else {
              // Wait for the token to be refreshed
              subscribeTokenRefresh((token: string) => {
                makeRequest(token);
              });
            }
          } else {
            reject(error);
          }

          if (
            error.status &&
            error.status >= 400 &&
            error.response?.data &&
            typeof error.response?.data === "object" &&
            "message" in error.response?.data &&
            typeof error.response?.data.message === "string" &&
            error.response?.data.message.startsWith("error.") &&
            // @ts-ignore
            typeof window.addToast === "function"
          ) {
            // @ts-ignore
            window.addToast({
              title: i18n.t("common.error"),
              description: i18n.t("error:::" + error.response?.data.message),
              variant: "error",
            });
          }
        });
    };

    makeRequest();
  });

  // Add cancel method to the promise
  (promise as any).cancel = () => {
    source.cancel("Query was cancelled");
  };

  return promise;
};

// Type definitions for error handling
export type ErrorType<Error> = AxiosError<Error>;
export type BodyType<BodyData> = BodyData;
